// NewProjectDialog — "New project" (#213, pen "Starting a project", PS-36 to
// PS-41; #312, pen "Ny projekt-ruta"): one box where a project is prepared
// before it exists. Every part is optional: a name, what it is about, its
// files, who it is for (under More details). Nothing is made until "Create
// project"; Cancel makes nothing. With uploaded sources in the workspace, the
// box widens to show "Your sources" beside the form: one click or one drag
// puts a source in Files.
//
// Pure: the draft, the rows, the panel's list and the create status are
// props, and every gesture is reported up. Local state is view-ephemeral only
// (whether a drag is over Files, whether the Files list is open, the keyboard
// line). The host (NewProjectHost) runs the ports.
// What is typed here is data about the project, never instructions: nothing
// here interprets it (SF-22). A drop on Files that is neither a file nor a
// known source is ignored without a word (SF-22).
//
// Files (pen "New project box: many files"): one compact line per file, three
// shown, the rest one "{n} more" away; open, the list grows to FILES_IN_VIEW
// lines and scrolls inside, so the box never grows past the screen.
//
// Modules, between the description and Files: an optional list of
// modules, guessed from the description when the person leaves it
// (newProjectModules.ts: their own rows win). Empty: the project starts
// empty. At the ceiling (CL-22) "Add a module" gives way to the limit hint.
// What the project is made for is never asked (owner, 31 Oct): the format
// is chosen at export.

export type { BoxSource, CreateStatus, NewProjectDraft };
export { NewProjectDialog };

import * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
    type DragEvent,
    type KeyboardEvent,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    addModuleRow,
    type ModulesDraft,
    modulesFull,
    removeModuleRow,
    renameModuleRow,
    withGuesses,
} from "../../app/newProjectModules.js";
import {
    BOX_FILES_MAX,
    FILES_IN_VIEW,
    fileListKey,
    foldFiles,
    type SourceKind,
    sourceDropped,
    sourceKindOf,
    type YourSource,
    type YourSourcesState,
} from "../../app/yourSources.js";
import { Button } from "../Button/Button.js";
import { RESOURCE_DRAG_MIME } from "../Composer/PowerComposer/resourceDrag.js";
import { Dialog } from "../Dialog/Dialog.js";
import { ErrorBanner } from "../ErrorBanner/ErrorBanner.js";
import { Field } from "../Field/Field.js";
import { Icon } from "../Icon/Icon.js";
import { Input } from "../Input/Input.js";
import { ProgressBar } from "../ProgressBar/ProgressBar.js";
import { Text } from "../Text/Text.js";
import { Textarea } from "../Textarea/Textarea.js";
import { kindIcon, YourSourcesPanel } from "./YourSourcesPanel.js";
import "./NewProjectDialog.css";

// What the author has written in the box so far (view-ephemeral until
// Create). One audience (Q14's default): picking another replaces it.
type NewProjectDraft = {
    readonly name: string;
    readonly about: string;
    readonly modules: ModulesDraft;
    readonly audience: string | null;
};

// One source row in the box. `key` is the row's own id: a picked source's id,
// or the host's ordinal for a file uploaded from the box. A ready row carries
// the source's id, so the panel shows it as Added.
type BoxSource =
    | {
          readonly type: "uploading";
          readonly key: string;
          readonly title: string;
      }
    | {
          readonly type: "ready";
          readonly key: string;
          readonly sourceId: string;
          readonly title: string;
          // Uploaded from this box, or picked from Uploaded sources.
          readonly origin: "uploaded" | "picked";
      }
    | {
          readonly type: "unreadable";
          readonly key: string;
          readonly title: string;
      };

// Where "Create project" stands.
type CreateStatus =
    | { readonly type: "ready" }
    // Pressed once: busy until it lands (one press, one project).
    | { readonly type: "creating" }
    // No connection: Create waits; what was typed stays.
    | { readonly type: "offline" }
    // The create was refused: the box stays, everything kept (Felruta).
    | {
          readonly type: "failed";
          readonly title: string;
          readonly body: string | undefined;
          readonly errorId: string;
      };

type Option = { readonly id: string; readonly title: string };

type NewProjectDialogProps = {
    readonly open: boolean;
    readonly draft: NewProjectDraft;
    readonly onDraftChange: (draft: NewProjectDraft) => void;
    // The name an empty Name field gets ("Untitled project" or the lowest
    // free "Untitled project {n}"), shown as its placeholder.
    readonly defaultName: string;
    readonly audiences: readonly Option[];
    readonly sources: readonly BoxSource[];
    // What is in Uploaded sources, for the Your sources panel (left out
    // when it is ready and empty).
    readonly yourSources: YourSourcesState;
    // Puts a source in Files, or takes it out when it is there (a toggle).
    readonly onPick: (source: YourSource) => void;
    readonly onRetrySources: () => void;
    readonly onShowAllSources: () => void;
    readonly onUpload: (files: readonly File[]) => void;
    readonly onRemove: (key: string) => void;
    // The size ceiling of one file, shown as a limit hint ("32 MB").
    readonly fileLimit: string;
    readonly status: CreateStatus;
    readonly onCreate: () => void;
    readonly onCancel: () => void;
};

// The share of a ceiling from which its hint or counter shows.
const NEAR_CEILING = 0.9;

const NewProjectDialog = ({
    open,
    draft,
    onDraftChange,
    defaultName,
    audiences,
    sources,
    yourSources,
    onPick,
    onRetrySources,
    onShowAllSources,
    onUpload,
    onRemove,
    fileLimit,
    status,
    onCreate,
    onCancel,
}: NewProjectDialogProps): ReactNode => {
    const { t } = useLingui();
    const nameRef = useRef<HTMLInputElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const filesRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const createRef = useRef<HTMLButtonElement>(null);
    const modulesRef = useRef<HTMLOListElement>(null);
    // View-ephemeral: whether a drag that Files takes is over it, whether the
    // Files list is open, the keyboard line, and a line to focus after a
    // keyboard removal re-renders the list.
    const [over, setOver] = useState(false);
    const [openFiles, setOpenFiles] = useState(false);
    const [line, setLine] = useState(0);
    const refocus = useRef<number | null>(null);
    // A module row to put the caret in after the list re-renders (one just
    // added).
    const caretIn = useRef<number | null>(null);
    useEffect(() => {
        if (caretIn.current !== null) {
            modulesRef.current
                ?.querySelectorAll("textarea")
                .item(caretIn.current)
                ?.focus();
            caretIn.current = null;
        }
        if (refocus.current === null) return;
        lineAt(listRef.current, refocus.current)?.focus();
        refocus.current = null;
    });
    const modules = draft.modules;
    const setModules = (next: ModulesDraft) =>
        onDraftChange({ ...draft, modules: next });
    const busy = status.type === "creating" || status.type === "offline";
    const create = () => {
        if (!busy) onCreate();
    };
    const shortMax = Core.Limits.SHORT_TEXT_MAX;
    const longMax = Core.Limits.LONG_TEXT_MAX;
    const modulesMax = Core.Limits.NEW_PROJECT_MODULES_MAX;
    const added = new Set(
        sources.flatMap((s) => (s.type === "ready" ? [s.sourceId] : [])),
    );
    const full =
        sources.filter((s) => s.type !== "unreadable").length >= BOX_FILES_MAX;
    const known = yourSources.type === "ready" ? yourSources.sources : [];
    const withPanel = !(
        yourSources.type === "ready" && yourSources.sources.length === 0
    );
    const fold = foldFiles(sources, openFiles);
    const here = Math.max(0, Math.min(line, fold.shown.length - 1));
    // A picked source wears its library kind; an upload, its name's.
    const kindOfFile = (s: BoxSource): SourceKind =>
        (s.type === "ready"
            ? known.find((k) => k.id === s.sourceId)?.kind
            : undefined) ?? sourceKindOf(s.title);
    // Show (Your sources): the Files list, open and in view.
    const showFiles = () => {
        setOpenFiles(true);
        filesRef.current?.scrollIntoView({ block: "nearest" });
    };
    const fileKey = (e: KeyboardEvent<HTMLUListElement>) => {
        const action = fileListKey(e.key, here, fold.shown.length);
        switch (action.type) {
            case "move":
                e.preventDefault();
                setLine(action.index);
                lineAt(listRef.current, action.index)?.focus();
                return;
            case "remove": {
                const s = fold.shown[action.index];
                if (s === undefined) return;
                e.preventDefault();
                const next = Math.max(
                    0,
                    Math.min(action.index, fold.shown.length - 2),
                );
                setLine(next);
                refocus.current = next;
                onRemove(s.key);
                return;
            }
            case "none":
                return;
            default:
                exhaustSwitchCase(action);
        }
    };
    // Files takes an OS file or a row from Your sources; nothing else.
    const takes = (e: DragEvent<HTMLDivElement>): boolean =>
        e.dataTransfer.types.includes("Files") ||
        e.dataTransfer.types.includes(RESOURCE_DRAG_MIME);
    const drop = (e: DragEvent<HTMLDivElement>) => {
        setOver(false);
        if (!takes(e)) return;
        // Claimed here: the window-wide drop (IngestHost) only disarms.
        e.preventDefault();
        if (e.dataTransfer.files.length > 0) {
            onUpload([...e.dataTransfer.files]);
            return;
        }
        const id = sourceDropped(
            e.dataTransfer.getData(RESOURCE_DRAG_MIME),
            known,
        );
        const source = known.find((s) => s.id === id);
        if (source !== undefined && !added.has(source.id) && !full)
            onPick(source);
    };
    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) onCancel();
            }}
            className={
                withPanel
                    ? "bilda-new-project bilda-new-project--with-sources"
                    : "bilda-new-project"
            }
            title={t`New project`}
            description={t`Say what you want to make. Everything is optional and can be changed later.`}
            initialFocusRef={nameRef}
            aside={
                withPanel ? (
                    <YourSourcesPanel
                        state={yourSources}
                        written={`${draft.name} ${draft.about}`}
                        added={added}
                        inFiles={sources.length}
                        full={full}
                        onAdd={onPick}
                        onRetry={onRetrySources}
                        onShowAll={onShowAllSources}
                        onShowFiles={showFiles}
                    />
                ) : undefined
            }
            footer={
                <>
                    {status.type === "offline" ? (
                        <Text
                            as="p"
                            tone="dim"
                            className="bilda-new-project__wait"
                        >
                            {t`You're offline – try again when you're back`}
                        </Text>
                    ) : null}
                    <Button variant="ghost" onClick={onCancel}>
                        {t`Cancel`}
                    </Button>
                    <Button
                        ref={createRef}
                        variant="primary"
                        onClick={create}
                        disabled={busy}
                        aria-busy={status.type === "creating" || undefined}
                    >
                        {t`Create project`}
                    </Button>
                </>
            }
        >
            <div className="bilda-new-project__body">
                {status.type === "failed" ? (
                    <ErrorBanner
                        severity="danger"
                        title={status.title}
                        body={status.body}
                        kept={t`Everything you wrote is kept.`}
                        errorId={status.errorId}
                    />
                ) : null}
                <Field
                    label={t`Name`}
                    htmlFor="new-project-name"
                    hint={
                        draft.name.length >= shortMax * NEAR_CEILING
                            ? t`A name can be up to ${shortMax} characters`
                            : undefined
                    }
                >
                    <Input
                        id="new-project-name"
                        inputRef={nameRef}
                        value={draft.name}
                        placeholder={defaultName}
                        maxLength={shortMax}
                        autoComplete="off"
                        onChange={(e) =>
                            onDraftChange({ ...draft, name: e.target.value })
                        }
                        onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            create();
                        }}
                    />
                </Field>
                <Field
                    label={t`What do you want to make?`}
                    htmlFor="new-project-about"
                    hint={
                        <span className="bilda-new-project__tip">
                            <Icon name="lightbulb" size="xs" />
                            <span>
                                {t`Say what it is, who it is for and what it covers. What it covers becomes the modules.`}
                                {draft.about.length >= longMax * NEAR_CEILING
                                    ? ` ${draft.about.length} / ${longMax}`
                                    : null}
                            </span>
                        </span>
                    }
                >
                    <Textarea
                        id="new-project-about"
                        value={draft.about}
                        rows={3}
                        maxLength={longMax}
                        onChange={(e) =>
                            onDraftChange({ ...draft, about: e.target.value })
                        }
                        // Guesses come when the person leaves the field, so
                        // the list never jumps while they type; not when they
                        // leave it for Create (a guess is never made unseen).
                        onBlur={(e) => {
                            if (e.relatedTarget === createRef.current) return;
                            setModules(withGuesses(modules, draft.about));
                        }}
                    />
                </Field>
                <section
                    className="bilda-new-project__section bilda-new-project__section--modules"
                    aria-labelledby="new-project-modules"
                >
                    <div className="bilda-new-project__files-head">
                        <span
                            id="new-project-modules"
                            className="bilda-new-project__label"
                        >
                            {t`Modules`}
                        </span>
                        <span className="bilda-new-project__count">
                            {t`optional, now or later in the outline`}
                        </span>
                    </div>
                    <div className="bilda-new-project__modules">
                        {modules.rows.length > 0 ? (
                            <ol
                                ref={modulesRef}
                                className="bilda-new-project__module-list"
                            >
                                {modules.rows.map((row, i) => (
                                    <li
                                        key={row.key}
                                        className="bilda-new-project__module"
                                    >
                                        <span
                                            className="bilda-new-project__module-number"
                                            aria-hidden="true"
                                        >
                                            {i + 1}
                                        </span>
                                        <textarea
                                            className="bilda-new-project__module-name"
                                            aria-label={t`Module ${i + 1}`}
                                            value={row.name}
                                            rows={1}
                                            maxLength={shortMax}
                                            autoComplete="off"
                                            onChange={(e) =>
                                                setModules(
                                                    renameModuleRow(
                                                        modules,
                                                        row.key,
                                                        e.target.value,
                                                    ),
                                                )
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter")
                                                    e.preventDefault();
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="bilda-new-project__file-remove"
                                            aria-label={t`Remove ${row.name}`}
                                            onClick={() =>
                                                setModules(
                                                    removeModuleRow(
                                                        modules,
                                                        row.key,
                                                    ),
                                                )
                                            }
                                        >
                                            <Icon name="close" size="xs" />
                                        </button>
                                    </li>
                                ))}
                            </ol>
                        ) : null}
                        {modulesFull(modules) ? (
                            <Text
                                as="p"
                                tone="dim"
                                className="bilda-new-project__hint bilda-new-project__hint--full bilda-new-project__module-limit"
                            >
                                <Icon name="info" size="xs" />
                                {t`Up to ${modulesMax} modules here. Add more later in the outline.`}
                            </Text>
                        ) : (
                            <button
                                type="button"
                                className="bilda-new-project__add-module"
                                onClick={() => {
                                    caretIn.current = modules.rows.length;
                                    setModules(addModuleRow(modules));
                                }}
                            >
                                <Icon name="plus" size="xs" />
                                {t`Add a module`}
                            </button>
                        )}
                    </div>
                    {modules.rows.some(
                        (r) => r.name.length >= shortMax * NEAR_CEILING,
                    ) ? (
                        <Text
                            as="p"
                            tone="dim"
                            className="bilda-new-project__hint"
                        >
                            {t`A name can be up to ${shortMax} characters`}
                        </Text>
                    ) : modules.rows.some((r) => r.origin === "guessed") ? (
                        <Text
                            as="p"
                            tone="dim"
                            className="bilda-new-project__hint"
                        >
                            {t`Guessed from your description; change or remove any. Leave it empty to start empty.`}
                        </Text>
                    ) : null}
                </section>
                {/* biome-ignore lint/a11y/noStaticElementInteractions: the drop target for files and sources; Upload files and the Your sources list are the keyboard ways in. */}
                <div
                    ref={filesRef}
                    className="bilda-new-project__section bilda-new-project__section--files"
                    onDragOver={(e) => {
                        if (!takes(e)) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "copy";
                        setOver(true);
                    }}
                    onDragLeave={(e) => {
                        if (
                            e.relatedTarget instanceof Node &&
                            e.currentTarget.contains(e.relatedTarget)
                        )
                            return;
                        setOver(false);
                    }}
                    onDrop={drop}
                >
                    <div className="bilda-new-project__files-head">
                        <span
                            id="new-project-files"
                            className="bilda-new-project__label"
                        >
                            {t`Files`}
                        </span>
                        {sources.length > 0 ? (
                            <span className="bilda-new-project__count">
                                {t`${plural(sources.length, {
                                    one: "# added",
                                    other: "# added",
                                })}`}
                            </span>
                        ) : null}
                    </div>
                    {sources.length > 0 ? (
                        <div className="bilda-new-project__files">
                            <ul
                                ref={listRef}
                                id="new-project-file-list"
                                aria-labelledby="new-project-files"
                                className="bilda-new-project__file-list"
                                style={{
                                    maxHeight: `calc(${FILES_IN_VIEW} * var(--size-control-md))`,
                                }}
                                onKeyDown={fileKey}
                            >
                                {fold.shown.map((s, i) => (
                                    <FileLine
                                        key={s.key}
                                        source={s}
                                        kind={kindOfFile(s)}
                                        index={i}
                                        current={i === here}
                                        onFocus={() => setLine(i)}
                                        onRemove={onRemove}
                                    />
                                ))}
                            </ul>
                            {fold.foldable ? (
                                <button
                                    type="button"
                                    className="bilda-new-project__fold"
                                    aria-expanded={openFiles}
                                    aria-controls="new-project-file-list"
                                    onClick={() => setOpenFiles(!openFiles)}
                                >
                                    <Icon
                                        name={
                                            openFiles
                                                ? "chevron-up"
                                                : "chevron-down"
                                        }
                                        size="xs"
                                    />
                                    {openFiles
                                        ? t`Show fewer`
                                        : t`${plural(fold.more, {
                                              one: "# more",
                                              other: "# more",
                                          })}`}
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    <div
                        className="bilda-new-project__drop"
                        data-over={over || undefined}
                    >
                        <Icon name="upload" size="sm" />
                        <span className="bilda-new-project__drop-text">
                            {sources.length > 0
                                ? t`Drop more here, or`
                                : t`Drop files here, or`}
                        </span>
                        <button
                            type="button"
                            className="bilda-new-project__upload"
                            disabled={full}
                            onClick={() => fileRef.current?.click()}
                        >
                            {t`Upload files`}
                        </button>
                        <input
                            ref={fileRef}
                            type="file"
                            multiple
                            hidden
                            onChange={(e) => {
                                onUpload([...(e.target.files ?? [])]);
                                e.target.value = "";
                            }}
                        />
                    </div>
                    <Text
                        as="p"
                        tone="dim"
                        className={
                            full
                                ? "bilda-new-project__hint bilda-new-project__hint--full"
                                : "bilda-new-project__hint"
                        }
                    >
                        <Icon name="info" size="xs" />
                        {t`PDF, Word, PowerPoint or text · up to ${fileLimit} a file · ${BOX_FILES_MAX} at a time`}
                    </Text>
                </div>
                {audiences.length > 0 ? (
                    <details className="bilda-new-project__more">
                        <summary className="bilda-new-project__more-head">
                            <Icon name="chevron-right" size="sm" />
                            <span className="bilda-new-project__label">
                                {t`More details`}
                            </span>
                            <span className="bilda-new-project__more-sum">
                                {t`Who it is for`}
                            </span>
                        </summary>
                        <fieldset className="bilda-new-project__section">
                            <legend className="bilda-new-project__label">
                                {t`Who is it for?`}
                            </legend>
                            <div className="bilda-new-project__chips">
                                {audiences.map((a) => {
                                    const on = draft.audience === a.id;
                                    return (
                                        <button
                                            key={a.id}
                                            type="button"
                                            className="bilda-new-project__chip"
                                            aria-pressed={on}
                                            onClick={() =>
                                                onDraftChange({
                                                    ...draft,
                                                    audience: on ? null : a.id,
                                                })
                                            }
                                        >
                                            <Icon name="person" size="sm" />
                                            <span>{a.title}</span>
                                            {on ? (
                                                <Icon name="close" size="sm" />
                                            ) : null}
                                        </button>
                                    );
                                })}
                            </div>
                        </fieldset>
                    </details>
                ) : null}
            </div>
        </Dialog>
    );
};

// The list's line at `index`, for keyboard focus.
const lineAt = (
    list: HTMLUListElement | null,
    index: number,
): HTMLElement | null => {
    const el = list?.children.item(index);
    return el instanceof HTMLElement ? el : null;
};

// One file in Files, on one line: its kind, its title (cut, the whole title
// on hover and for screen readers), where it stands when not ready, and × to
// take it out of the box (an uploaded file stays in Uploaded sources). The
// list is one tab stop: only the current line takes Tab; × is for the mouse
// (Delete or Backspace on the line does the same).
const FileLine = ({
    source,
    kind,
    index,
    current,
    onFocus,
    onRemove,
}: {
    readonly source: BoxSource;
    readonly kind: SourceKind;
    readonly index: number;
    readonly current: boolean;
    readonly onFocus: () => void;
    readonly onRemove: (key: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <li
            className="bilda-new-project__file"
            data-index={index}
            data-state={source.type}
            tabIndex={current ? 0 : -1}
            title={source.title}
            onFocus={onFocus}
        >
            <span className="bilda-new-project__file-kind" aria-hidden="true">
                <Icon name={kindIcon(kind)} size="xs" />
            </span>
            <span className="bilda-new-project__file-title">
                {source.title}
            </span>
            <FileState source={source} />
            <button
                type="button"
                tabIndex={-1}
                className="bilda-new-project__file-remove"
                aria-label={t`Remove ${source.title} from the box`}
                onClick={() => onRemove(source.key)}
            >
                <Icon name="close" size="xs" />
            </button>
        </li>
    );
};

// Where a file stands, when it is not simply in: a thin bar while it uploads,
// a warning word when it could not be read.
const FileState = ({ source }: { readonly source: BoxSource }): ReactNode => {
    const { t } = useLingui();
    switch (source.type) {
        case "ready":
            return null;
        case "uploading":
            return (
                <ProgressBar
                    tone="accent"
                    label={t`Uploading…`}
                    className="bilda-new-project__file-progress"
                />
            );
        case "unreadable":
            return (
                <span className="bilda-new-project__file-warning">
                    {t`Could not be read`}
                </span>
            );
        default:
            return exhaustSwitchCase(source);
    }
};
